import React from 'react';
import { ChevronDownIcon, Trash2Icon } from 'lucide-react';
import { FormData, RuleOperator, VisibilityRule } from '../../types';
import { conditionMet, describeRule, fieldCatalog } from '../../utils/config';
import { Select } from './AdminControls';
export const OPERATORS: {
  value: RuleOperator;
  label: string;
}[] = [{
  value: 'equals',
  label: 'is'
}, {
  value: 'notEquals',
  label: 'is not'
}, {
  value: 'isEmpty',
  label: 'is empty'
}, {
  value: 'isNotEmpty',
  label: 'has a value'
}];
interface RuleRowProps {
  rule: VisibilityRule;
  values: FormData;
  fieldOptions: {
    value: string;
    label: string;
  }[];
  expanded: boolean;
  onToggleExpand: () => void;
  onUpdate: (patch: Partial<VisibilityRule>) => void;
  onDelete: () => void;
}
export function RuleRow({
  rule,
  values,
  fieldOptions,
  expanded,
  onToggleExpand,
  onUpdate,
  onDelete
}: RuleRowProps) {
  const active = rule.enabled && conditionMet(rule, values);
  const conditionField = fieldCatalog[rule.conditionFieldId];
  const needsValue = rule.operator === 'equals' || rule.operator === 'notEquals';
  return <li className={`overflow-hidden rounded-xl border transition-colors ${active ? 'border-blue-300 bg-blue-50/40' : 'border-slate-200 bg-white'} ${rule.enabled ? '' : 'opacity-60'}`} data-id="element-72" data-mp="1v6is0o485pgz">
      <div className="flex items-center gap-2 px-3 py-2" data-id="element-73" data-mp="256jcwehqsdna">
        <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${rule.action === 'hide' ? 'bg-slate-200 text-slate-600' : 'bg-emerald-100 text-emerald-700'}`} data-id="element-74" data-mp="2973a1i2bfh9f">
          {rule.action}
        </span>

        <button type="button" onClick={onToggleExpand} aria-expanded={expanded} className="min-w-0 flex-1 text-left" data-id="element-75" data-mp="2nvpyc99x7jcs">
          <span className="block truncate text-xs font-semibold text-slate-800" data-id="element-76" data-mp="2sszppugve539">
            {fieldCatalog[rule.targetFieldId]?.label ?? 'Select a field'}
          </span>
          <span className="block truncate text-[11px] text-slate-500" data-id="element-77" data-mp="xsfplj5f8a6t">{describeRule(rule)}</span>
        </button>

        {active && <span className="shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-700" data-id="element-78" data-mp="sr06vxqpufrl">
            Active
          </span>}

        <button type="button" role="switch" aria-checked={rule.enabled} aria-label={`${rule.enabled ? 'Disable' : 'Enable'} rule`} onClick={() => onUpdate({
        enabled: !rule.enabled
      })} className={`relative h-4 w-8 shrink-0 rounded-full transition-colors ${rule.enabled ? 'bg-blue-600' : 'bg-slate-300'}`} data-id="element-79" data-mp="11rer0try1oti">
          <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-all ${rule.enabled ? 'left-[18px]' : 'left-0.5'}`} data-id="element-80" data-mp="1lfe2y7y8v20i" />
        </button>

        <button type="button" onClick={onToggleExpand} aria-label={expanded ? 'Collapse rule' : 'Edit rule'} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100" data-id="element-81" data-mp="3nb814d677ljx">
          <ChevronDownIcon className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} data-id="element-82" data-mp="33d0zimnz8844" />
        </button>
      </div>

      {expanded && <div className="grid grid-cols-2 gap-2 border-t border-slate-200 bg-slate-50 p-3" data-id="element-83" data-mp="2tg5z1y857xn7">
          <Select label="Action" value={rule.action} onChange={v => onUpdate({
        action: v as 'hide' | 'show'
      })} options={[{
        value: 'hide',
        label: 'Hide field'
      }, {
        value: 'show',
        label: 'Show field'
      }]} data-id="element-84" data-mp="2ygtrmoc0smjj" />
          <Select label="Target field" value={rule.targetFieldId} onChange={v => onUpdate({
        targetFieldId: v
      })} options={fieldOptions} data-id="element-85" data-mp="ipfneo9frfa4" />
          <Select label="When field" value={rule.conditionFieldId} onChange={v => onUpdate({
        conditionFieldId: v
      })} options={fieldOptions} data-id="element-86" data-mp="2jpsv1yy4vpir" />
          <Select label="Condition" value={rule.operator} onChange={v => onUpdate({
        operator: v as RuleOperator
      })} options={OPERATORS} data-id="element-87" data-mp="29rd4py6xkkab" />
          {needsValue && <div className="col-span-2" data-id="element-88" data-mp="20u2u8f1xsz6g">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-89" data-mp="1w19obnuqfkss">
                Value
              </span>
              {conditionField?.options?.length ? <Select value={rule.conditionValue} ariaLabel="Condition value" onChange={v => onUpdate({
          conditionValue: v
        })} options={[{
          value: '',
          label: '— Select —'
        }, ...conditionField.options.map(o => ({
          value: o,
          label: o
        }))]} data-id="element-90" data-mp="1m453brcmhvdk" /> : <input value={rule.conditionValue} aria-label="Condition value" onChange={e => onUpdate({
          conditionValue: e.target.value
        })} placeholder="Enter a value" className="w-full rounded-lg border border-slate-300 px-2.5 py-2 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-id="element-91" data-mp="3tnsns2ddahqk" />}
            </div>}

          <div className="col-span-2 flex justify-end" data-id="element-92" data-mp="3eywg46f836qh">
            <button type="button" onClick={onDelete} className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50" data-id="element-93" data-mp="3jr7gfadvkujk">
              <Trash2Icon className="h-3.5 w-3.5" data-id="element-94" data-mp="3ulkbtlef1h3j" />
              Delete rule
            </button>
          </div>
        </div>}
    </li>;
}